// Card-of-the-Day daily local notification scheduler.
// Schedules a recurring 9 AM local notification on the user's device.
// Tapping the notification opens the app at /tarot.
// All plugin calls are guarded — safe on web (no-op).

import { isNativeApp } from "./platform";

const ENABLED_KEY = "tara_daily_reading_enabled";
const NOTIFICATION_ID = 7777;

export function isDailyReadingEnabled() {
  return localStorage.getItem(ENABLED_KEY) === "1";
}

export async function enableDailyReading(hour = 9, minute = 0) {
  localStorage.setItem(ENABLED_KEY, "1");
  if (!isNativeApp()) return { scheduled: false, reason: "web" };

  try {
    const { LocalNotifications } = await import("@capacitor/local-notifications");
    const perm = await LocalNotifications.requestPermissions();
    if (perm.display !== "granted") {
      localStorage.setItem(ENABLED_KEY, "0");
      return { scheduled: false, reason: "denied" };
    }

    // Cancel any prior schedule so we don't stack duplicates
    try {
      const pending = await LocalNotifications.getPending();
      const mine = pending.notifications.filter(n => n.id === NOTIFICATION_ID);
      if (mine.length) await LocalNotifications.cancel({ notifications: mine });
    } catch { /* ignore */ }

    await LocalNotifications.schedule({
      notifications: [{
        id: NOTIFICATION_ID,
        title: "Your Card of the Day awaits ✨",
        body: "Tap to reveal what the cosmos has lined up for you.",
        schedule: { on: { hour, minute }, repeats: true, allowWhileIdle: true },
        extra: { route: "/tarot" },
      }],
    });
    return { scheduled: true };
  } catch (e) {
    return { scheduled: false, reason: String(e) };
  }
}

export async function disableDailyReading() {
  localStorage.setItem(ENABLED_KEY, "0");
  if (!isNativeApp()) return;
  try {
    const { LocalNotifications } = await import("@capacitor/local-notifications");
    await LocalNotifications.cancel({ notifications: [{ id: NOTIFICATION_ID }] });
  } catch { /* ignore */ }
}

// Wire tap → navigate. Call once at app bootstrap.
export async function attachNotificationTapHandler(navigate) {
  if (!isNativeApp()) return;
  try {
    const { LocalNotifications } = await import("@capacitor/local-notifications");
    LocalNotifications.addListener("localNotificationActionPerformed", (event) => {
      const route = event?.notification?.extra?.route;
      if (route && typeof navigate === "function") navigate(route);
    });
  } catch { /* ignore */ }
}
